import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { BeforeAfterSlider } from "@/components/BeforeAfterSlider";
import { Lightbox } from "@/components/Lightbox";

export default function ProjectDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [lb, setLb] = useState(null);
  const { scrollYProgress } = useScroll();
  const heroY = useTransform(scrollYProgress, [0, 0.3], [0, 150]);

  useEffect(() => {
    api.get(`/projects/${slug}`).then((r) => setP(r.data)).catch(() => setP(false));
  }, [slug]);

  if (p === false) return <div className="pt-40 text-center min-h-screen">Project not found. <Link to="/projects" className="underline">Back</Link></div>;
  if (!p) return <div className="pt-40 text-center min-h-screen text-onyx-metal">Loading…</div>;

  const gallery = (p.gallery || []).map(fileUrl);

  return (
    <div>
      <section className="relative h-screen overflow-hidden bg-onyx-black" data-testid="project-hero">
        <motion.img style={{ y: heroY }} src={fileUrl(p.hero_image)} alt={p.name} className="absolute inset-0 w-full h-[120%] object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-black/40" />
        <div className="relative h-full max-w-[1600px] mx-auto px-6 lg:px-12 flex flex-col justify-end pb-20">
          <Link to="/projects" className="text-white/60 hover:text-white text-sm flex items-center gap-2 mb-8 transition-colors"><ArrowLeft size={16} /> All Projects</Link>
          <div className="flex flex-wrap gap-3 mb-6">
            {[p.type, p.location, p.year].filter(Boolean).map((t) => (
              <span key={t} className="text-[10px] tracking-[0.2em] uppercase bg-white/10 backdrop-blur-md text-white px-3 py-1.5 border border-white/20">{t}</span>
            ))}
          </div>
          <h1 className="text-white font-display font-extrabold text-5xl md:text-8xl tracking-tightest leading-[0.9] max-w-4xl">{p.name}</h1>
        </div>
      </section>

      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 py-24 md:py-32 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-3">
          <span className="text-[11px] tracking-[0.3em] uppercase text-onyx-metal">Overview</span>
        </div>
        <Reveal className="md:col-span-9">
          <p className="font-display font-semibold text-2xl md:text-4xl tracking-tight leading-[1.25]">{p.overview}</p>
        </Reveal>
      </section>

      {/* facts */}
      <section className="border-y border-black/10">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid grid-cols-2 md:grid-cols-4 divide-x divide-black/10">
          {[["Location", p.location], ["Type", p.type], ["Completed", p.completion_date || p.year], ["Year", p.year]].map(([k, v]) => (
            <div key={k} className="py-10 px-4 first:pl-0">
              <div className="text-[10px] tracking-[0.2em] uppercase text-onyx-metal">{k}</div>
              <div className="font-display font-bold text-lg mt-2">{v || "—"}</div>
            </div>
          ))}
        </div>
      </section>

      {/* scope + materials */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 py-24 grid md:grid-cols-2 gap-16">
        <Reveal>
          <h2 className="font-display font-extrabold text-3xl tracking-tightest mb-8">Scope of Work</h2>
          <ul className="space-y-4">
            {(p.scope_of_work || []).map((s, i) => (
              <li key={i} className="flex gap-4 border-b border-black/10 pb-4">
                <span className="font-mono text-onyx-wood text-sm">0{i + 1}</span>
                <span className="text-onyx-charcoal">{s}</span>
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-display font-extrabold text-3xl tracking-tightest mb-8">Systems & Materials</h2>
          <div className="flex flex-wrap gap-3">
            {(p.systems_materials || []).map((m, i) => (
              <span key={i} className="border border-black/15 px-4 py-2.5 text-sm">{m}</span>
            ))}
          </div>
        </Reveal>
      </section>

      {/* before/after */}
      {p.before_image && p.after_image && (
        <section className="bg-onyx-charcoal py-24" data-testid="project-beforeafter">
          <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
            <h2 className="font-display font-extrabold text-3xl md:text-5xl tracking-tightest text-white mb-4">From site to system.</h2>
            <p className="text-white/50 mb-10">Drag the handle to reveal the transformation.</p>
            <BeforeAfterSlider before={fileUrl(p.before_image)} after={fileUrl(p.after_image)} className="h-[360px] md:h-[600px]" />
          </div>
        </section>
      )}

      {/* gallery */}
      {gallery.length > 0 && (
        <section className="max-w-[1600px] mx-auto px-6 lg:px-12 py-24" data-testid="project-gallery">
          <h2 className="font-display font-extrabold text-3xl md:text-5xl tracking-tightest mb-10">Gallery</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {gallery.map((img, i) => (
              <Reveal key={i} delay={(i % 2) * 0.08}>
                <button onClick={() => setLb(i)} className={`group relative w-full overflow-hidden block ${i % 3 === 0 ? "h-[520px]" : "h-[380px]"}`}>
                  <img src={img} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </button>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      <Lightbox images={gallery} index={lb} onClose={() => setLb(null)} onNav={(d) => setLb((v) => (v + d + gallery.length) % gallery.length)} />

      <section className="bg-onyx-black text-white py-24 text-center">
        <h2 className="font-display font-extrabold text-3xl md:text-5xl tracking-tightest px-6">Have a similar project?</h2>
        <Link to="/contact" className="inline-block mt-8 bg-white text-onyx-black px-10 py-4 text-[12px] tracking-[0.15em] uppercase hover:bg-onyx-wood transition-colors">Request a Proposal</Link>
      </section>
    </div>
  );
}
